import React, { useState } from 'react'

const PAD = { left: 28, right: 12, top: 14, bottom: 28 }
const VB_W = 440
const VB_H = 160
const PLOT_W = VB_W - PAD.left - PAD.right
const PLOT_H = VB_H - PAD.top - PAD.bottom

function toPlotY(value) {
  return PAD.top + PLOT_H - ((value - 1) / 4) * PLOT_H
}

function toPlotX(index, total) {
  if (total === 1) return PAD.left + PLOT_W / 2
  return PAD.left + (index / (total - 1)) * PLOT_W
}

function catmullRomPath(points) {
  if (points.length < 2) return ''
  if (points.length === 2) {
    return `M${points[0].x},${points[0].y}L${points[1].x},${points[1].y}`
  }

  const tension = 0.5
  let d = `M${points[0].x},${points[0].y}`

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]

    const cp1x = p1.x + (p2.x - p0.x) / (6 * tension)
    const cp1y = p1.y + (p2.y - p0.y) / (6 * tension)
    const cp2x = p2.x - (p3.x - p1.x) / (6 * tension)
    const cp2y = p2.y - (p3.y - p1.y) / (6 * tension)

    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${p2.x},${p2.y}`
  }

  return d
}

const dateFmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })

function formatDate(iso) {
  return dateFmt.format(new Date(iso))
}

function pickLabelIndices(total, max) {
  if (total <= max) return Array.from({ length: total }, (_, i) => i)
  const indices = [0]
  const step = (total - 1) / (max - 1)
  for (let i = 1; i < max - 1; i++) {
    indices.push(Math.round(step * i))
  }
  indices.push(total - 1)
  return indices
}

export function QuickPulseSparkline({ entries, colors, labels, zones }) {
  const [hovered, setHovered] = useState(null)

  if (!entries || entries.length === 0) return null

  const points = entries.map((e, i) => ({
    x: toPlotX(i, entries.length),
    y: toPlotY(e.value),
    value: e.value,
    date: e.createdAt,
  }))

  const pathD = catmullRomPath(points)
  const labelIndices = pickLabelIndices(entries.length, 6)
  const earliest = formatDate(entries[0].createdAt)
  const latest = formatDate(entries[entries.length - 1].createdAt)

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      role="img"
      aria-label={`Trend chart showing ${entries.length} sessions from ${earliest} to ${latest}`}
      style={{ display: 'block' }}
    >
      {zones && zones.map((zone, i) => {
        const yTop = toPlotY(zone.max)
        const yBottom = toPlotY(zone.min)
        return (
          <rect
            key={`zone-${i}`}
            x={PAD.left}
            y={yTop}
            width={PLOT_W}
            height={yBottom - yTop}
            fill={zone.color}
            opacity={0.1}
            rx={2}
          />
        )
      })}

      {[1, 2, 3, 4, 5].map((level) => {
        const y = toPlotY(level)
        return (
          <g key={level}>
            <line
              x1={PAD.left}
              y1={y}
              x2={VB_W - PAD.right}
              y2={y}
              stroke="var(--border)"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <text
              x={PAD.left - 6}
              y={y + 4}
              textAnchor="end"
              fontSize={12}
              fill="var(--text-secondary)"
            >
              {level}
            </text>
          </g>
        )
      })}

      {labelIndices.map((idx) => (
        <text
          key={idx}
          x={points[idx].x}
          y={VB_H - 4}
          textAnchor="middle"
          fontSize={12}
          fill="var(--text-secondary)"
        >
          {formatDate(entries[idx].createdAt)}
        </text>
      ))}

      {pathD && (
        <path
          d={pathD}
          fill="none"
          stroke="var(--accent-brand, #3b6d11)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {points.map((pt, i) => {
        const isLast = i === points.length - 1
        const isHovered = hovered === i
        const baseR = isLast ? 4 : 3
        const r = isHovered ? 5 : baseR

        return (
          <g
            key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            role="button"
            aria-label={`Session ${formatDate(pt.date)}: ${labels[pt.value] || pt.value}`}
            style={{ cursor: 'pointer' }}
          >
            <circle cx={pt.x} cy={pt.y} r={12} fill="transparent" />

            {isLast && (
              <circle
                cx={pt.x}
                cy={pt.y}
                r={r + 2}
                fill="var(--surface, #fff)"
                stroke="none"
              />
            )}
            <circle
              cx={pt.x}
              cy={pt.y}
              r={r}
              fill={colors[pt.value] || 'var(--text-secondary)'}
            />
          </g>
        )
      })}

      {hovered !== null && <Tooltip point={points[hovered]} colors={colors} labels={labels} />}
    </svg>
  )
}

function Tooltip({ point, colors, labels }) {
  const flipLeft = point.x > VB_W / 2
  const tx = flipLeft ? point.x - 8 : point.x + 8
  const ty = point.y - 28
  const anchor = flipLeft ? 'end' : 'start'
  const rectX = flipLeft ? -86 : -4
  const date = formatDate(point.date)
  const label = labels[point.value] || String(point.value)

  return (
    <g transform={`translate(${tx},${ty})`} style={{ pointerEvents: 'none' }}>
      <rect
        x={rectX}
        y={-14}
        width={90}
        height={34}
        rx={4}
        fill="var(--surface, #fff)"
        stroke="var(--border)"
        strokeWidth={1}
      />
      <text
        x={flipLeft ? -4 : 4}
        y={2}
        textAnchor={anchor}
        fontSize={12}
        fill="var(--text-secondary)"
      >
        {date}
      </text>
      <text
        x={flipLeft ? -4 : 4}
        y={16}
        textAnchor={anchor}
        fontSize={12}
        fontWeight={600}
        fill={colors[point.value] || 'var(--text-primary)'}
      >
        {label}
      </text>
    </g>
  )
}

export function SparklineLoading() {
  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" style={{ display: 'block' }}>
      {[0.25, 0.5, 0.75].map((frac, i) => (
        <circle
          key={i}
          cx={PAD.left + frac * PLOT_W}
          cy={PAD.top + PLOT_H / 2}
          r={4}
          fill="var(--border)"
          style={{
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </svg>
  )
}

export function SparklineError() {
  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" style={{ display: 'block' }}>
      <text
        x={VB_W / 2}
        y={VB_H / 2}
        textAnchor="middle"
        fontSize={14}
        fill="var(--text-secondary)"
      >
        Couldn&apos;t load history
      </text>
    </svg>
  )
}
